import React, { useEffect, useRef } from 'react';

import {
  BOOKMARK_ADD_TO_ZIP,
  BOOKMARK_REMOVE_FROM_ZIP,
  BOOKMARK_REMOVE_GROUP_FROM_ZIP,
  SET_BOOKMARK_EXPANDED,
} from '../../context/bookmark/reducer';

import CardLoginRequired from '../card/card-login-required';
import { DOWNLOAD_TYPE, PAGE_VARIANT } from '../../constants';

import { getConfig, getTranslation } from '../../utils/config';
import slugify from 'slugify';
import { useGroupAttributes } from '../../hooks/use-attributes';
import { type IBookmarkContext, useDfContext } from '../../context/df-context';
import { REMOVE_BOOKMARK_ITEM, REMOVE_BOOKMARK_GROUP } from '../../context/actions';
import useMediaQuery from '../../hooks/use-media-query';
import MobileCardView from '../card/mobile-card-view';
import { BookmarkGroupView } from './bookmark-group-view';
import type { IBookmarkGroup, IItem } from '../../types';

export default function BookmarkGroup({
  groupId,
  bookmarkGroup,
}: {
  bookmarkGroup: IBookmarkGroup;
  groupId: number;
}): React.ReactElement {
  const isMobile = useMediaQuery('(max-width: 581px)');

  const {
    state: { selectedBookmarks, expanded, attributes, loggedIn },
    dispatch,
  } = useDfContext<IBookmarkContext>();
  const [forceLogin, setForceLogin] = React.useState(false);
  const preventDownload = useRef(!loggedIn);
  const domain = getConfig('download_url_prefix', '');
  const cmsNativeLanguage = getConfig('cms_native_language', 'EN-EN')?.toLocaleLowerCase();

  const loginRequired = React.useMemo(
    () => bookmarkGroup.items.some((item: IItem) => item.type === DOWNLOAD_TYPE.LOGIN),
    [bookmarkGroup],
  );

  const categorySlug = `df-icon-fallback df-icon-${slugify(String(bookmarkGroup.dfCategoryIndependent), { lower: true })}`;

  const handleSelection = (id: number) => (e: React.FormEvent<HTMLInputElement>) => {
    if (loginRequired) {
      setForceLogin(true);
    } else {
      const { checked } = e.currentTarget;
      dispatch({
        type: checked ? BOOKMARK_ADD_TO_ZIP : BOOKMARK_REMOVE_FROM_ZIP,
        item: id,
      });
    }
  };

  const handleDownload = (e: React.MouseEvent) => {
    if (!loginRequired) return;
    if (preventDownload.current) {
      e.preventDefault();
      preventDownload.current = !loggedIn;
      if (loggedIn) {
        (e.target as HTMLAnchorElement).click();
      } else {
        setForceLogin(true);
      }
    }
  };

  const handleRemove = () => {
    bookmarkGroup?.items.forEach((item: IItem) => {
      dispatch({
        type: BOOKMARK_REMOVE_GROUP_FROM_ZIP,
        item: item.pimId,
      });
    });
    dispatch({
      type: REMOVE_BOOKMARK_GROUP,
      group: groupId,
    });
  };

  const handleRemoveItem = (itemId: number) => {
    dispatch({
      type: REMOVE_BOOKMARK_ITEM,
      item: itemId,
    });
  };

  const groupAttributes = useGroupAttributes(bookmarkGroup?.items || [], attributes);

  const isExpanded = expanded === bookmarkGroup?.pimId;

  const getDownloadlink = (file: IItem) => `${domain}/${cmsNativeLanguage}/download/${file.pimId}`;

  const toggleExpanded = (toggle?: boolean) => {
    dispatch({
      type: SET_BOOKMARK_EXPANDED,
      expanded: toggle ? null : bookmarkGroup?.pimId,
    });
  };

  useEffect(() => {
    if (loggedIn && forceLogin) {
      setForceLogin(false);
    }
  }, [loggedIn, forceLogin]);

  return (
    <article className={`df-list-module df-list-module-bookmark ${categorySlug}`}>
      <span className="df-list-module__download-type">{bookmarkGroup?.category}</span>
      <h3 className="df-list-module__headline">{bookmarkGroup?.title}</h3>
      {isMobile ? (
        <MobileCardView
          id={groupId}
          taggingProduct={[]}
          text={bookmarkGroup.text || ''}
          childOrder={[]}
          loginRequired={loginRequired}
          cardData={bookmarkGroup.items}
          groupAttributes={groupAttributes}
          handleDownload={handleDownload}
          variant={PAGE_VARIANT.BOOKMARK}
          dfSubtitle={bookmarkGroup.dfSubtitle}
          removeItem={handleRemoveItem}
        />
      ) : (
        <BookmarkGroupView
          isExpanded={isExpanded}
          bookmarkGroup={bookmarkGroup}
          toggleExpanded={toggleExpanded}
          groupAttributes={groupAttributes}
          handleSelection={handleSelection}
          loginRequired={loginRequired}
          selectedBookmarks={selectedBookmarks || []}
          getDownloadlink={getDownloadlink}
          handleDownload={handleDownload}
          groupId={groupId}
          removeItem={handleRemoveItem}
        />
      )}
      <button
        title={getTranslation('bookmark_removed_title')}
        onClick={handleRemove}
        className="df-icon-btn df-btn-special__delete"
      >
        <svg className="df-action-icon">
          <use href={getConfig('material_symbols_url') + '#delete_400'} />
        </svg>
      </button>
      {forceLogin && <CardLoginRequired handleClose={() => setForceLogin(false)} />}
    </article>
  );
}
